'use client';
import React, { useState, useCallback } from 'react';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { PricePredictionChart } from './PricePredictionChart';
import { PricePredictionTable } from './PricePredictionTable';
import { YearlyPricePrediction } from './YearlyPricePrediction';
import { PricePredictionButton } from './PricePredictionButton';
import { YearlyPriceData } from './types';

export interface PricePredictionProps {
  type?: 'stock' | 'crypto';
  coin_id?: string;
  symbol?: string;
  btn_link?: string;
  btn_label?: string;
  hide_btn?: boolean;
  predictions?: { [year: number]: YearlyPriceData }[] | null;
  year?: number;
  display_mode?: 'chart' | 'table' | 'both';
  disclosure?: string;
}

export const PricePrediction: React.FC<PricePredictionProps> = ({
  btn_label,
  btn_link,
  disclosure,
  display_mode = 'both',
  hide_btn = false,
  predictions,
  symbol = '',
  type = 'stock',
  year,
}) => {
  const defaultLayout = display_mode === 'both' ? 'chart' : display_mode;
  const [activeLayout, setActiveLayout] = useState<'chart' | 'table'>(defaultLayout);

  const tabStyles = useCallback(
    (tabType: 'chart' | 'table') => ({
      backgroundColor: activeLayout === tabType ? 'rgb(24, 121, 255)' : '#e1ebfa',
      border: 'none',
      borderRadius: '4px',
      color: activeLayout === tabType ? 'white' : undefined,
      fontWeight: '600',
      padding: '6px',
      width: '50%',
    }),
    [activeLayout],
  );

  const predictionData = predictions?.[symbol];
  if (!predictionData || Object.keys(predictionData).length === 0) {
    return null;
  }

  return (
    <div className="block w-full my-4">
      {year && year > 0 ? (
        <>
          <YearlyPricePrediction
            btn_label={btn_label}
            btn_link={btn_link}
            hide_btn={hide_btn}
            predictions={predictions}
            symbol={symbol}
            year={year}
          />
        </>
      ) : (
        <>
          {predictionData && Object.keys(predictionData).length > 0 && (
            <div className="price-pred-chart my-5 bg-white box-border flex flex-col justify-center p-4 pb-6 shadow-sm border border-t-4 border-t-[rgb(255,90,31)]">
              {display_mode == 'both' ? (
                <div className="flex space-x-1 mb-5">
                  <button
                    onClick={() => {
                      setActiveLayout('chart');
                    }}
                    style={tabStyles('chart')}
                    type="button"
                  >
                    Chart
                  </button>
                  <button
                    onClick={() => {
                      setActiveLayout('table');
                    }}
                    style={tabStyles('table')}
                    type="button"
                  >
                    Table
                  </button>
                </div>
              ) : null}
              {activeLayout === 'chart' ? (
                <PricePredictionChart predictionData={predictionData} type={type} />
              ) : (
                <PricePredictionTable predictionData={predictionData} />
              )}
              {btn_link && btn_label && !hide_btn && (
                <PricePredictionButton btn_label={btn_label} btn_link={btn_link} />
              )}
              {disclosure && (
                <p className="italic leading-5 mt-2">
                  <small dangerouslySetInnerHTML={{ __html: sanitizeHTML(disclosure) }} />
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
